/**
 * /partner/links – Link-Generator. Universal-Link + ein paar kuratierte Deeplinks.
 * Bewusst minimal: keine vollautomatische Produktliste, damit wir hier nicht
 * jeden Katalog-Change durchschleppen müssen. Erweiterbar.
 */
import { useMemo, useState } from "react";
import PartnerLayout from "@/components/partner/PartnerLayout";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useMyAffiliate } from "@/hooks/useMyAffiliate";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Copy, Check } from "lucide-react";
import { toast } from "@/hooks/use-toast";

const PRESETS: Array<{ label: string; path: string }> = [
  { label: "Startseite", path: "/" },
  { label: "Mitgliedschaft (Monatlich)", path: "/abo-checkout/pro?interval=monthly" },
  { label: "Mitgliedschaft (Jährlich)", path: "/abo-checkout/pro?interval=yearly" },
  { label: "Fachkraft Musik", path: "/ausbildung/fachkraft-musik" },
  { label: "Fachkraft Gitarre", path: "/ausbildung/fachkraft-gitarre" },
  { label: "Alle Kurse", path: "/kurse" },
  { label: "Alle Termine", path: "/termine" },
];

const BASE_URL = "https://www.musikonzept.de";
const DISCOUNT_CODE = "FREUNDE10";

const CopyRow = ({ value }: { value: string }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast({ title: "Link kopiert" });
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast({ title: "Kopieren fehlgeschlagen", variant: "destructive" });
    }
  };
  return (
    <div className="flex gap-2">
      <Input readOnly value={value} className="font-mono text-xs" />
      <Button type="button" variant="outline" size="icon" onClick={handleCopy} aria-label="Link kopieren">
        {copied ? <Check className="h-4 w-4 text-primary" /> : <Copy className="h-4 w-4" />}
      </Button>
    </div>
  );
};

const PartnerLinks = () => {
  const { data: user } = useCurrentUser();
  const { data: affiliate } = useMyAffiliate(user?.id);
  const [customPath, setCustomPath] = useState("");

  const code = affiliate?.affiliate_code ?? "";

  const buildLink = (pathOrUrl: string) => {
    const trimmed = pathOrUrl.trim();
    // Vollständige URL? Dann nur Tracking-Parameter ergänzen.
    const isAbsolute = /^https?:\/\//i.test(trimmed);
    const url = isAbsolute
      ? new URL(trimmed)
      : new URL(trimmed.startsWith("/") ? trimmed : `/${trimmed}`, BASE_URL);
    url.searchParams.set("ref", code);
    url.searchParams.set("coupon", DISCOUNT_CODE);
    return url.toString();
  };

  const customLink = useMemo(
    () => (customPath.trim() ? buildLink(customPath.trim()) : ""),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customPath, code],
  );

  return (
    <PartnerLayout title="Meine Links">
      <div className="grid gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Universal-Link</CardTitle>
            <CardDescription>
              Funktioniert für alle Seiten – Cookie bleibt 60 Tage gesetzt. Der Rabattcode {DISCOUNT_CODE} (10%) ist
              automatisch enthalten.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <CopyRow value={buildLink("/")} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Direkt-Links zu Produkten</CardTitle>
            <CardDescription>Optimal für gezielte Empfehlungen.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {PRESETS.map((p) => (
              <div key={p.path}>
                <div className="mb-1 text-sm font-medium">{p.label}</div>
                <CopyRow value={buildLink(p.path)} />
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Eigener Link</CardTitle>
            <CardDescription>Pfad einer beliebigen Seite eintragen, z.B. /kurse/musik-mit-naturmaterialien</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input
              placeholder="/kurse/dein-kurs"
              value={customPath}
              onChange={(e) => setCustomPath(e.target.value)}
            />
            {customLink && <CopyRow value={customLink} />}
          </CardContent>
        </Card>
      </div>
    </PartnerLayout>
  );
};

export default PartnerLinks;
